import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Check, ClipboardList, Hourglass, Link2, Mail, Minus, Pencil, PenLine, Plus, Send, Snowflake, X } from "lucide-react";
import { api } from "./api";
import { Badge, BoutonSombre, Carte, EnTeteMenu, Modale, Recherche } from "./ui";
import { useConfirmation } from "./Confirmation";

/* Le spectacle de Noël dans le menu Spectacles (Jen, 31.10.2026 : « faudrait y inclure le
   spectacle de Noël avec la liste des réservations »). Les réservations sont gratuites, faites
   sur le site (Inscription → Spectacle → Noël) : les chiffres clés de la salle, la liste
   d'attente par date de demande, puis chaque réservation.

   Jen, 02.11.2026 : « On doit tracker ça par date de demande, premier arrivé premier servi »,
   « je ne peux pas modifier le nombre par famille ou annuler une réservation depuis cet
   écran ». Chaque réservation se modifie (nombre de personnes, une hausse seulement dans les
   places libres) ou s'annule ; les places rendues vont aussitôt à la liste d'attente. Les
   familles ont aussi leur lien « Modifier ou annuler ma réservation » (/mon-spectacle) :
   celles confirmées avant ce lien le reçoivent quand l'école clique « Envoyer le lien ».
   La feuille imprimable reste dans Liste → Spectacles. */

const aller = (detail) => window.dispatchEvent(new CustomEvent("console-aller", { detail }));
const TON = (s) => (/^annul/i.test(s) ? "rouge" : /^liste/i.test(s) ? "ambre" : "vert");
const rangTexte = (n) => (n === 1 ? "1er" : `${n}e`);
const personnes = (n) => `${n} personne${n > 1 ? "s" : ""}`;
const placesLibres = (n) => (n > 0 ? `${n} place${n > 1 ? "s" : ""} libre${n > 1 ? "s" : ""}` : "aucune place libre");
const nomDe = (l) => `${l.prenom || ""} ${l.nom || ""}`.trim() || "cette famille";
const ROUGE = { background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" };

/* « Place donnée à … » après une action qui a fait tourner la liste d'attente. */
const phrasePromus = (promus) => (promus?.length
  ? ` Liste d'attente servie : ${promus.length > 1 ? "les demandes de" : "la demande de"} ${promus.map((p) => `${p.nom} (${p.places} pers.)`).join(", ")} ${promus.length > 1 ? "sont confirmées, les familles prévenues" : "est confirmée, la famille prévenue"} par e-mail.`
  : "");

function ListeAttente({ salle, onDonner }) {
  const attente = salle?.attente || [];
  const total = attente.reduce((n, a) => n + (a.places || 0), 0);
  return (
    <section className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: "#FDE68A" }} data-testid="noel-attente">
      <header className="px-4 md:px-5 py-4 flex flex-wrap items-center gap-3" style={{ background: "#FFFBEB", borderBottom: "1px solid #FDE68A" }}>
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#D97706", color: "#fff" }}>
          <Hourglass size={17} />
        </span>
        <div className="min-w-[13rem] flex-1">
          <h2 className="font-display text-lg tracking-tight leading-tight" style={{ color: "#0F1B2D" }}>Liste d'attente</h2>
          <p className="text-xs mt-0.5" style={{ color: "#92400E" }} data-testid="noel-attente-regle">
            Premier arrivé, premier servi : dès qu'une place se libère, la console confirme automatiquement la
            première demande qui rentre (dans les 5 minutes) et prévient la famille par e-mail.
          </p>
        </div>
        <Badge ton={attente.length ? "ambre" : "neutre"}>
          {attente.length ? `${attente.length} demande${attente.length > 1 ? "s" : ""} · ${personnes(total)}` : "Personne n'attend"}
        </Badge>
      </header>
      {attente.length > 0 ? (
        <ol>
          {attente.map((a) => (
            <li key={a.id} className="px-4 md:px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-b last:border-b-0"
              style={{ borderColor: "var(--border-soft)" }} data-testid="noel-attente-ligne">
              <span className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
                style={{ background: a.rang === 1 ? "#0F1B2D" : "#F1F5F9", color: a.rang === 1 ? "#fff" : "#334155" }}>
                {rangTexte(a.rang)}
              </span>
              <div className="min-w-[11rem] flex-1">
                <div className="font-semibold">{nomDe(a)}</div>
                <div className="text-xs text-[var(--muted)]">{a.demande_le ? `Demandé le ${a.demande_le}` : "Date de demande inconnue"}</div>
              </div>
              <div className="text-sm whitespace-nowrap"><strong className="text-base">{a.places}</strong> place{a.places > 1 ? "s" : ""}</div>
              <span title={a.rentre ? "" : `Groupe de ${a.places} : ${placesLibres(a.libres)} dans la salle`}>
                <Badge ton={a.rentre ? "vert" : "ambre"}>
                  {a.rentre ? "Rentre dans les places libres" : `Attend ${a.manque} place${a.manque > 1 ? "s" : ""}`}
                </Badge>
              </span>
              <button className={`${a.rentre ? "btn-primary" : "btn-ghost"} text-[11px] !py-1 !px-2.5 whitespace-nowrap`}
                onClick={() => onDonner(a)} data-testid="noel-donner">
                <Check size={12} /> Donner la place
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <p className="px-5 py-4 text-sm text-[var(--muted)]">Personne n'attend pour l'instant.</p>
      )}
    </section>
  );
}

function LiensFamilles({ liens, onEnvoyer, onExemple, enCours }) {
  if (!liens) return null;
  const n = liens.familles || 0;
  return (
    <section className="bg-white rounded-2xl border px-4 md:px-5 py-4 flex flex-wrap items-center gap-3" style={{ borderColor: "var(--border)" }}
      data-testid="noel-liens">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#EBFBFC", color: "#0E7A85" }}>
        <Link2 size={17} />
      </span>
      <div className="min-w-[13rem] flex-1">
        <div className="text-sm font-semibold">Lien « Modifier ou annuler ma réservation »</div>
        <div className="text-xs text-[var(--muted)] mt-0.5" data-testid="noel-liens-texte">
          {liens.passe ? "Le spectacle a eu lieu : plus de lien à envoyer."
            : n ? `${n} famille${n > 1 ? "s" : ""} confirmée${n > 1 ? "s" : ""} ${n > 1 ? "n'ont" : "n'a"} pas encore reçu ${n > 1 ? "leur" : "son"} lien personnel. Les nouvelles réservations le reçoivent d'office dans leur e-mail.`
              : "Toutes les familles confirmées ont leur lien personnel (dans leur e-mail)."}
        </div>
      </div>
      <button className="btn-ghost text-xs" onClick={onExemple} disabled={!!enCours} data-testid="noel-liens-exemple">
        <Mail size={13} /> M'envoyer un exemple
      </button>
      {/* Le même lien, dans un message écrit par l'école : Messages › Envois groupés, avec le
          modèle « libérer les places » et le bouton personnel de chaque famille (Jen, 03.11.2026). */}
      {!liens.passe && (
        <button className="btn-ghost text-xs" data-testid="noel-liens-ecrire"
          onClick={() => window.dispatchEvent(new CustomEvent("console-aller", { detail: {
            onglet: "envois", id: `spectacles::${liens.spectacle}##modele=spectacle-noel-liberer-places` } }))}>
          <PenLine size={13} /> Écrire aux familles…
        </button>
      )}
      {n > 0 && !liens.passe && (
        <button className="btn-primary text-xs" onClick={onEnvoyer} disabled={!!enCours} data-testid="noel-liens-envoyer">
          <Send size={13} /> Envoyer le lien de modification aux familles confirmées
        </button>
      )}
    </section>
  );
}

function Modifier({ ligne, salle, onFermer, onFait }) {
  const avant = Number(ligne.nb_personnes || 0);
  const [n, setN] = useState(avant || 1);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState("");
  const maxi = salle?.max_par_reservation || 7;
  const enAttente = TON(ligne.statut) === "ambre";
  const libres = salle?.libres ?? 0;
  const possible = enAttente ? maxi : Math.min(maxi, avant + libres);
  const trop = n > possible;

  const enregistrer = async () => {
    setEnCours(true);
    setErreur("");
    try {
      const r = await api(`/spectacles/reservations/${encodeURIComponent(ligne.id)}/modifier`, {
        method: "POST", body: JSON.stringify({ nb_personnes: n }),
      });
      onFait(`Réservation de ${r.qui} : ${r.avant} → ${personnes(r.places)}.${phrasePromus(r.promus)}`);
    } catch (e) {
      setErreur(e.message);
      setEnCours(false);
    }
  };

  const rond = "w-11 h-11 rounded-full flex items-center justify-center border disabled:opacity-30";
  return (
    <Modale titre={`Modifier la réservation de ${nomDe(ligne)}`} onFermer={onFermer} testid="noel-modale-modifier"
      sous_titre={enAttente ? "En liste d'attente : le nombre change librement, la demande garde son rang."
        : `Aujourd'hui ${personnes(avant)} · ${placesLibres(libres)} dans la salle`}>
      <div className="flex items-center justify-center gap-6 py-2">
        <button type="button" className={rond} style={{ borderColor: "var(--border)" }} disabled={n <= 1}
          onClick={() => setN(n - 1)} aria-label="Une personne de moins" data-testid="noel-modifier-moins"><Minus size={18} /></button>
        <div className="text-center min-w-[5rem]">
          <div className="font-display text-5xl leading-none" data-testid="noel-modifier-n">{n}</div>
          <div className="text-xs text-[var(--muted)] mt-1">{n > 1 ? "personnes" : "personne"} (1 à {maxi})</div>
        </div>
        <button type="button" className={rond} style={{ borderColor: "var(--border)" }} disabled={n >= maxi}
          onClick={() => setN(n + 1)} aria-label="Une personne de plus" data-testid="noel-modifier-plus"><Plus size={18} /></button>
      </div>
      {trop && (
        <div className="rounded-xl px-4 py-3 text-sm mt-3" style={ROUGE} data-testid="noel-modifier-refus">
          {libres > 0 ? `Plus que ${placesLibres(libres)} : impossible de passer à ${n} personnes.`
            : `Plus aucune place libre : la salle est complète. Impossible de passer à ${n} personnes.`}
        </div>
      )}
      {!trop && !enAttente && n < avant && (
        <p className="text-xs mt-3 text-center" style={{ color: "#15803D" }}>
          {avant - n === 1 ? "1 place rendue" : `${avant - n} places rendues`} : la liste d'attente en profite tout de suite.
        </p>
      )}
      {erreur && <div className="rounded-xl px-4 py-3 text-sm mt-3" style={ROUGE}>{erreur}</div>}
      <div className="flex flex-wrap gap-2 mt-5">
        <button className="btn-ghost" onClick={onFermer} data-testid="noel-modifier-fermer">Fermer</button>
        <BoutonSombre onClick={enregistrer} disabled={enCours || n === avant || trop} testid="noel-modifier-enregistrer">
          <Check size={14} /> {enCours ? "Un instant…" : `Enregistrer ${personnes(n)}`}
        </BoutonSombre>
      </div>
    </Modale>
  );
}

export default function SpectacleNoel() {
  const { demander } = useConfirmation();
  const [salle, setSalle] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [liens, setLiens] = useState(null);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [q, setQ] = useState("");
  const [edition, setEdition] = useState(null);
  const [enCours, setEnCours] = useState("");
  const charger = useCallback(async () => {
    setErreur("");
    try {
      const s = ((await api("/spectacles/salles")).salles || [])[0] || null;
      setSalle(s);
      const liste = (await api("/liste/spectacles")).items || [];
      const titre = s?.spectacle || liste[0]?.id;
      setFiche(titre ? await api(`/liste/spectacles/fiche?id=${encodeURIComponent(titre)}`).catch(() => ({ lignes: [], entete: { titre } })) : { lignes: [] });
      if (s?.spectacle) setLiens(await api(`/spectacles/liens?spectacle=${encodeURIComponent(s.spectacle)}`).catch(() => null));
    } catch (e) { setErreur(e.message); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  // Les annulées restent visibles, grisées, en bas de la liste.
  const lignes = useMemo(() => (fiche?.lignes || [])
    .filter((l) => !q.trim()
      || `${l.prenom} ${l.nom} ${l.email} ${l.telephone}`.toLowerCase().includes(q.trim().toLowerCase()))
    .map((l, i) => ({ l, i }))
    .sort((a, b) => (TON(a.l.statut) === "rouge") - (TON(b.l.statut) === "rouge") || a.i - b.i)
    .map(({ l }) => l), [fiche, q]);
  if (!salle && !fiche) return <p className="text-[var(--muted)]">{erreur || "Chargement…"}</p>;
  const titre = salle?.spectacle || fiche?.entete?.titre || "Spectacle de Noël";
  const toutes = fiche?.lignes || [];
  const confirmees = toutes.filter((l) => TON(l.statut) === "vert");
  const attente = salle?.attente || [];
  const fait = (texte) => { setMessage(texte); setErreur(""); setEdition(null); charger(); };

  const donner = async (a) => {
    const depasse = !a.rentre;
    if (!await demander({
      titre: `Donner la place à ${nomDe(a)} ?`,
      ton: depasse ? "attention" : undefined,
      texte: depasse
        ? `${personnes(a.places)}, mais ${placesLibres(salle?.libres || 0)} : la salle passera à ${(salle?.occupees || 0) + a.places} / ${salle?.capacite} (à assumer côté sécurité). La famille reçoit l'e-mail « une place s'est libérée » avec son lien personnel.`
        : `${personnes(a.places)} : la famille reçoit l'e-mail « une place s'est libérée » avec son lien personnel.`,
      valider: depasse ? "Donner la place quand même" : "Donner la place",
    })) return;
    try {
      await api(`/spectacles/reservations/${encodeURIComponent(a.id)}/confirmer`, { method: "POST" });
      fait(`Place donnée à ${nomDe(a)} : e-mail envoyé.`);
    } catch (e) { setErreur(e.message); }
  };

  const annuler = async (l) => {
    const enAttente = TON(l.statut) === "ambre";
    const n = Number(l.nb_personnes || 0);
    const rep = await demander({
      titre: enAttente ? `Retirer ${nomDe(l)} de la liste d'attente ?` : `Annuler la réservation de ${nomDe(l)} ?`,
      ton: "danger",
      texte: enAttente
        ? `La demande de ${personnes(n)} sort de la liste d'attente. Elle reste visible, grisée, en bas de la liste.`
        : `${n > 1 ? `Les ${n} places sont libérées` : "La place est libérée"} : la liste d'attente en profite tout de suite (la première demande qui rentre est confirmée et prévenue). La réservation reste visible, grisée, en bas de la liste.`,
      option: { libelle: "Prévenir la famille par e-mail", cochee: true },
      valider: enAttente ? "Retirer de la liste" : "Annuler la réservation",
      annuler: "Garder la réservation",
    });
    if (!rep) return;
    try {
      const r = await api(`/spectacles/reservations/${encodeURIComponent(l.id)}/annuler`, {
        method: "POST", body: JSON.stringify({ prevenir: !!rep.option }),
      });
      fait(`Réservation de ${r.qui} annulée${r.email_envoye_a ? " : la famille est prévenue par e-mail" : ""}.${phrasePromus(r.promus)}`);
    } catch (e) { setErreur(e.message); }
  };

  const envoyerLiens = async () => {
    const n = liens?.familles || 0;
    if (!n || !await demander({
      titre: `Envoyer le lien à ${n} famille${n > 1 ? "s" : ""} ?`,
      texte: `${n > 1 ? `Chacune des ${n} familles confirmées reçoit` : "La famille confirmée reçoit"} une seule fois l'e-mail « Ta réservation pour le Spectacle de Noël 🎄 » avec son lien personnel « Modifier ou annuler ma réservation ».`,
      points: [`${liens.reservations} réservation${liens.reservations > 1 ? "s" : ""} (une famille avec deux réservations reçoit un seul e-mail, avec un lien pour chacune).`,
        "Les familles qui ont déjà leur lien ne reçoivent rien.",
        "L'envoi se fait en arrière-plan : compte quelques minutes."],
      valider: `Envoyer à ${n} famille${n > 1 ? "s" : ""}`,
    })) return;
    setEnCours("envoi");
    try {
      const r = await api("/spectacles/liens/envoyer", { method: "POST", body: JSON.stringify({ spectacle: titre, familles: n }) });
      fait(`Lien en route vers ${r.familles} famille${r.familles > 1 ? "s" : ""}.${r.mode_reel ? "" : " Mode test : les e-mails sont seulement notés dans Messages › E-mails envoyés."}`);
    } catch (e) { setErreur(e.message); }
    setEnCours("");
  };

  const exemple = async () => {
    setEnCours("exemple");
    try {
      const r = await api("/spectacles/liens/exemple", { method: "POST", body: JSON.stringify({ spectacle: titre }) });
      setMessage(`${r.message} Son lien ouvre une réservation fictive : rien ne s'enregistre.${r.mode_reel ? "" : " (Mode test : l'e-mail est seulement noté dans Messages › E-mails envoyés.)"}`);
    } catch (e) { setErreur(e.message); }
    setEnCours("");
  };

  return (
    <div className="space-y-5" data-testid="console-spectacle-noel">
      <EnTeteMenu ton="annexes" Icon={Snowflake} contexte="Spectacles 2026-2027" titre={titre}
        texte="Réservations gratuites, faites sur le site."
        points={[
          [Hourglass, "Liste d'attente", "Premier arrivé, premier servi"],
          [ArrowRightLeft, "Place libérée", "Va toute seule à la famille suivante"],
          [Link2, "Lien des familles", "Modifier ou annuler en deux clics"],
        ]}
        droite={salle && <Badge ton={salle.libres ? "vert" : "rouge"}>{salle.libres ? placesLibres(salle.libres) : "Complet"}</Badge>} testid="noel-entete" />
      {message && <div className="rounded-2xl px-4 py-3 text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }} data-testid="noel-message">{message}</div>}
      {erreur && <div className="rounded-2xl px-4 py-3 text-sm" style={ROUGE} data-testid="noel-erreur">{erreur}</div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="noel-chiffres">
        <Carte titre="Places réservées" valeur={salle ? `${salle.occupees} / ${salle.capacite}` : "—"} detail={salle ? placesLibres(salle.libres) : ""} ton="primaire" />
        <Carte titre="Réservations" valeur={confirmees.length} detail={`${toutes.length} au total, annulées comprises`} />
        <Carte titre="Liste d'attente" valeur={attente.length} ton={attente.length ? "ambre" : "neutre"}
          detail={attente.length ? `${attente.reduce((n, l) => n + (l.places || 0), 0)} personnes attendent` : "personne n'attend"} />
        <Carte titre="Annulées" valeur={toutes.filter((l) => TON(l.statut) === "rouge").length} detail="places rendues" />
      </div>
      <ListeAttente salle={salle} onDonner={donner} />
      <LiensFamilles liens={liens} onEnvoyer={envoyerLiens} onExemple={exemple} enCours={enCours} />
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary text-xs" onClick={() => aller({ onglet: "envois", id: `spectacles::${titre}` })} data-testid="noel-ecrire"><Mail size={13} /> Écrire aux familles</button>
        <button className="btn-ghost text-xs" onClick={() => aller({ onglet: "liste", id: `spectacles::${titre}` })} data-testid="noel-feuille"><ClipboardList size={13} /> Feuille imprimable (Liste)</button>
        <span className="ml-auto"><Recherche valeur={q} onChange={setQ} placeholder="Nom, e-mail ou téléphone" testid="noel-recherche" largeur={240} /></span>
      </div>
      <div className="bg-white rounded-2xl border overflow-x-auto" style={{ borderColor: "var(--border)" }} data-testid="noel-reservations">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-[var(--muted)] border-b" style={{ borderColor: "var(--border)" }}>
              <th className="px-3 py-2 font-semibold">Nom</th><th className="px-3 py-2 font-semibold text-right">Personnes</th>
              <th className="px-3 py-2 font-semibold">Contact</th><th className="px-3 py-2 font-semibold">État</th><th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => {
              const annulee = TON(l.statut) === "rouge";
              return (
                <tr key={l.id || `${l.prenom}-${l.nom}-${l.email}`} className="border-b last:border-b-0"
                  style={{ borderColor: "var(--border-soft)", opacity: annulee ? 0.45 : 1, background: annulee ? "#F8FAFC" : undefined }}
                  data-testid={annulee ? "noel-ligne-annulee" : "noel-ligne"}>
                  <td className="px-3 py-2"><strong>{l.prenom} {l.nom}</strong></td>
                  <td className="px-3 py-2 text-right"><strong className="text-base">{l.nb_personnes || 0}</strong></td>
                  <td className="px-3 py-2 text-[12px] text-[var(--muted)]">{[l.email, l.telephone].filter(Boolean).join(" · ")}</td>
                  <td className="px-3 py-2"><Badge ton={TON(l.statut)}>{l.statut || "Confirmé"}</Badge></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!annulee && l.id && (
                      <span className="inline-flex gap-1.5">
                        <button className="btn-ghost text-[11px] !py-1 !px-2.5" onClick={() => { setMessage(""); setEdition(l); }} data-testid="noel-modifier">
                          <Pencil size={12} /> Modifier
                        </button>
                        <button className="btn-ghost text-[11px] !py-1 !px-2.5 !text-[var(--muted)] hover:!text-[#B91C1C]" onClick={() => annuler(l)} data-testid="noel-annuler">
                          <X size={12} /> Annuler la réservation
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!lignes.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-[var(--muted)]">Aucune réservation.</td></tr>}
          </tbody>
          {lignes.length > 1 && (
            <tfoot><tr className="border-t text-xs" style={{ borderColor: "var(--border)" }}>
              <td className="px-3 py-2 text-[var(--muted)]">{lignes.length} réservations</td>
              <td className="px-3 py-2 text-right font-semibold">{lignes.filter((l) => TON(l.statut) === "vert").reduce((n, l) => n + Number(l.nb_personnes || 0), 0)}</td>
              <td colSpan={3} className="px-3 py-2 text-[var(--muted)]">personnes confirmées</td>
            </tr></tfoot>
          )}
        </table>
      </div>
      {edition && <Modifier ligne={edition} salle={salle} onFermer={() => setEdition(null)} onFait={fait} />}
    </div>
  );
}
